import { isEqual } from 'lodash';

import { TABLE_FILTER_OPERATORS } from 'utils/constants';

type MultiFilterValue = { key: string; value: string[] };

const isValidFilterValue = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  !Object.values(TABLE_FILTER_OPERATORS).includes(value);

export const getTableColumnFilterValues = (
  filters: Record<string, unknown> | undefined,
  columnKey: string,
  filterKey: string = columnKey
): string[] => {
  const columnFilters = filters?.[columnKey];

  if (!Array.isArray(columnFilters) || !columnFilters.length) {
    return [];
  }

  if (
    typeof columnFilters[0] === 'object' &&
    columnFilters[0] !== null &&
    'key' in columnFilters[0]
  ) {
    const values = (columnFilters as MultiFilterValue[]).find(
      ({ key }) => key === filterKey
    )?.value;
    return (values ?? []).filter(isValidFilterValue);
  }

  if (filterKey === columnKey) {
    return columnFilters.filter(isValidFilterValue);
  }

  return [];
};

export const getTableSiteFilterIds = (filters: Record<string, unknown> | undefined): string[] =>
  getTableColumnFilterValues(filters, 'site');

export const getTableTurbineFilterIds = (filters: Record<string, unknown> | undefined): string[] =>
  getTableColumnFilterValues(filters, 'turbine');

export const hasTableSiteFilter = (filters: Record<string, unknown> | undefined): boolean =>
  getTableSiteFilterIds(filters).length > 0;

const hasFilterValues = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

const areFilterValuesEqual = (firstValue: unknown, secondValue: unknown): boolean =>
  isEqual(firstValue ?? [], secondValue ?? []);

const clearStaleChildFilter = (
  filters: Record<string, unknown>,
  previousFilters: Record<string, unknown> | undefined,
  childKey: string
): boolean => {
  if (
    hasFilterValues(previousFilters?.[childKey]) &&
    areFilterValuesEqual(filters[childKey], previousFilters?.[childKey])
  ) {
    delete filters[childKey];
    return true;
  }

  return false;
};

export const clearStaleAssetHierarchyFilters = (
  filters: Record<string, unknown> | undefined,
  previousFilters: Record<string, unknown> | undefined
): Record<string, unknown> => {
  const originalFilters = filters ?? {};
  const nextFilters = { ...originalFilters };
  let changed = false;

  if (!previousFilters) {
    return originalFilters;
  }

  if (!areFilterValuesEqual(nextFilters.site, previousFilters.site)) {
    changed = clearStaleChildFilter(nextFilters, previousFilters, 'turbine') || changed;
    // Component filters can be stored under either key depending on the table.
    changed = clearStaleChildFilter(nextFilters, previousFilters, 'asset') || changed;
    changed = clearStaleChildFilter(nextFilters, previousFilters, 'component') || changed;
  }

  if (!areFilterValuesEqual(nextFilters.turbine, previousFilters.turbine)) {
    changed = clearStaleChildFilter(nextFilters, previousFilters, 'asset') || changed;
    changed = clearStaleChildFilter(nextFilters, previousFilters, 'component') || changed;
  }

  return changed ? nextFilters : originalFilters;
};
